import { createAsyncThunk } from '@reduxjs/toolkit';

import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type { HomeData, SearchResult, UltimoAcesso } from '../../../types/home/types';

interface ProcessoRecente {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  valorCausa?: number;
  dataDistribuicao?: string;
}

interface BackendSearchResultItem {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
  status?: string;
  date?: string;
  score?: number;
  metadata?: Record<string, unknown>;
}

interface GlobalSearchResult {
  processos: BackendSearchResultItem[];
  notificacoes: BackendSearchResultItem[];
  negociacoes: BackendSearchResultItem[];
  peticionamentos: BackendSearchResultItem[];
  documentos: BackendSearchResultItem[];
  totalResults: number;
}

interface DashboardResponse {
  volumeMetrics: HomeData['volumeMetrics'];
  financialMetrics: HomeData['financialMetrics'];
  criticalAlerts: HomeData['criticalAlerts'];
  notificationCount: number;
  urgentAlerts: HomeData['urgentAlerts'];
  processosRecentes: ProcessoRecente[];
  processosArbitrais: HomeData['processosArbitrais'];
  notificacoesExtrajudiciais: HomeData['notificacoesExtrajudiciais'];
  user?: HomeData['user'];
}

interface FetchAcessosParams {
  page?: number;
  limit?: number;
}

export const fetchHomeData = createAsyncThunk<HomeData>(
  'home/fetchHomeData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiGet('/dashboard');

      if (!response.ok) {
        throw new Error('Erro ao buscar dados da dashboard');
      }

      const raw = await response.json();
      const payload = (raw as { data?: DashboardResponse }).data ?? (raw as DashboardResponse);

      return {
        user: payload.user || null,
        volumeMetrics: payload.volumeMetrics,
        financialMetrics: payload.financialMetrics,
        criticalAlerts: payload.criticalAlerts,
        notificationCount: payload.notificationCount,
        urgentAlerts: payload.urgentAlerts,
        processosArbitrais: payload.processosArbitrais,
        notificacoesExtrajudiciais: payload.notificacoesExtrajudiciais,
      } satisfies HomeData;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const getDashboardSearch = createAsyncThunk<SearchResult[], string>(
  'home/getDashboardSearch',
  async (query, { rejectWithValue }) => {
    try {
      const response = await apiGet(`/dashboard/search?q=${encodeURIComponent(query)}`);

      if (!response.ok) {
        throw new Error('Erro ao buscar dados');
      }

      const raw = await response.json();
      const payload = (raw as { data: GlobalSearchResult }).data;

      if (!payload) return [];

      const allResults: SearchResult[] = [
        ...(payload.processos || []),
        ...(payload.notificacoes || []),
        ...(payload.negociacoes || []),
        ...(payload.peticionamentos || []),
        ...(payload.documentos || []),
      ]
        .map((item) => ({
          id: item.id,
          type: item.type as SearchResult['type'],
          title: item.title,
          subtitle: item.subtitle,
          description: item.description,
          status: item.status,
          date: item.date ? new Date(item.date).toLocaleDateString('pt-BR') : undefined,
          score: item.score,
          metadata: item.metadata,
        }))
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

      return allResults;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const fetchAcessos = createAsyncThunk<UltimoAcesso[], FetchAcessosParams | undefined>(
  'home/fetchAcessos',
  async (params, { rejectWithValue }) => {
    try {
      const page = params?.page ?? 1;
      const limit = params?.limit ?? 5;

      const response = await apiGet(`/acessos?page=${page}&limit=${limit}`);

      if (!response.ok) throw new Error('Erro ao buscar acessos');

      const json = await response.json();
      const data = (json as { data?: UltimoAcesso[] }).data || (json as UltimoAcesso[]);

      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const registrarAcesso = createAsyncThunk<UltimoAcesso, string>(
  'home/registrarAcesso',
  async (pagina, { rejectWithValue }) => {
    try {
      const response = await apiPost('/acessos', { pagina });

      if (!response.ok) throw new Error('Erro ao registrar acesso');

      return (await response.json()) as UltimoAcesso;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);
